import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Radio,
  Play,
  ArrowRight,
  Clock,
  BarChart3,
  User,
  Award,
  Calendar,
} from 'lucide-react';
import { formatET, getETAbbr } from '@/utils/dateUtils';

// HR Compliance industry check for CPE badge display
const HR_COMPLIANCE_NAMES = ['HR Compliance', 'HR compliance', 'Human Resources Compliance'];

const isHRCompliance = (industryName, subIndustryName) => {
  if (!industryName && !subIndustryName) return false;
  const nameToCheck = subIndustryName || industryName || '';
  return HR_COMPLIANCE_NAMES.some((hr) =>
    nameToCheck.toLowerCase().includes(hr.toLowerCase())
  );
};

const formatDayDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  return formatET(dateStr, 'EEE, MMM dd, yyyy');
};

// Light multi-color palette for industry tags (assigned deterministically per label)
const TAG_COLORS = [
  'text-blue-700 bg-blue-50 border-blue-200',
  'text-emerald-700 bg-emerald-50 border-emerald-200',
  'text-violet-700 bg-violet-50 border-violet-200',
  'text-amber-700 bg-amber-50 border-amber-200',
  'text-rose-700 bg-rose-50 border-rose-200',
  'text-teal-700 bg-teal-50 border-teal-200',
  'text-indigo-700 bg-indigo-50 border-indigo-200',
  'text-cyan-700 bg-cyan-50 border-cyan-200',
  'text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200',
  'text-sky-700 bg-sky-50 border-sky-200',
];

const getTagColor = (label = '') => {
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash + label.charCodeAt(i)) % TAG_COLORS.length;
  return TAG_COLORS[hash];
};

/**
 * WebinarCard - Image-free, MailCraft-style card used across all pages.
 *
 * @param {Object} item - Webinar or Seminar data object
 * @param {string} type - 'webinar' or 'seminar'
 * @param {number} animationDelay - Animation delay in seconds (optional)
 */
export const WebinarCard = ({ item, type = 'webinar', animationDelay = 0 }) => {
  const isWebinar = type === 'webinar';
  const detailUrl = isWebinar
    ? `/webinars/${item.slug || item.id}`
    : `/seminars/${item.slug || item.id}`;

  const itemType = isWebinar ? item.webinar_type : item.seminar_type;
  const isLive = itemType === 'live';

  const showCPE = !isLive && isHRCompliance(item.industry_name, item.sub_industry_name) && item.cpe_credits > 0;

  const speakerName =
    item.speaker_name ||
    (item.speakers?.[0]
      ? `${item.speakers[0].first_name || ''} ${item.speakers[0].last_name || ''}`.trim()
      : null) ||
    'TBA';

  const itemCode = isWebinar
    ? item.webinar_code || `GRC-WEB-${item.id?.slice(-4) || '0000'}`
    : item.seminar_code || `GRC-SEM-${item.id?.slice(-4) || '0000'}`;

  const industryLabel = item.sub_industry_name || item.industry_name || 'General';

  return (
    <Link to={detailUrl} className="block h-full">
      <div
        className="group relative h-full flex flex-col bg-white border border-zinc-200 rounded-2xl p-6 hover:border-zinc-900 hover:shadow-[0_16px_48px_-20px_rgba(15,23,42,0.25)] transition-all duration-200 animate-fade-in opacity-0"
        style={{ animationDelay: `${animationDelay}s` }}
        data-testid={`${type}-card-${item.id}`}
      >
        {/* Header row: industry pill + type/CPE badges */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <span className={`mono-label text-[10px] rounded-full px-2.5 py-1 truncate max-w-[200px] border ${getTagColor(industryLabel)}`}>
            {industryLabel}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            {showCPE && (
              <span className="mono-label inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-1">
                <Award className="w-3 h-3" />
                CPE
              </span>
            )}
            {isLive ? (
              <span className="mono-label inline-flex items-center gap-1 text-[10px] text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
                <Radio className="w-3 h-3" />
                Live
              </span>
            ) : (
              <span className="mono-label inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-1">
                <Play className="w-3 h-3" />
                Recorded
              </span>
            )}
          </div>
        </div>

        {/* Code */}
        <span className="font-mono text-[13px] font-bold text-zinc-900 tracking-wider mb-2.5">{itemCode}</span>

        {/* Title */}
        <h3 className="font-heading text-xl md:text-2xl font-bold text-zinc-950 mb-5 leading-[1.15] line-clamp-2 group-hover:text-zinc-700 transition-colors">
          {item.title}
        </h3>

        {/* Meta - plain text with icons (no badge style) */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
          {isLive && (
            <span className="inline-flex items-center gap-1.5 text-[13px] text-zinc-600 font-medium">
              <Calendar className="w-4 h-4 text-zinc-400" />
              {item.scheduled_date ? formatDayDate(item.scheduled_date) : 'TBD'}
              {item.scheduled_date && <> · {formatET(item.scheduled_date, 'h:mm a')} {getETAbbr(item.scheduled_date)}</>}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-[13px] text-zinc-600 font-medium">
            <Clock className="w-4 h-4 text-zinc-400" />
            {item.duration_minutes || item.duration || 60} min
          </span>
          <span className="inline-flex items-center gap-1.5 text-[13px] text-zinc-600 font-medium capitalize">
            <BarChart3 className="w-4 h-4 text-zinc-400" />
            {item.level || 'Intermediate'}
          </span>
        </div>

        {/* Footer: speaker + price/CTA */}
        <div className="mt-auto flex items-center justify-between pt-5 border-t border-zinc-100">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-900 leading-none truncate">{speakerName}</div>
            <div className="mono-label text-[10px] text-zinc-400 mt-1.5">Speaker</div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="font-heading font-bold text-2xl text-zinc-950">${item.price}</span>
            <span className="w-10 h-10 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200 flex items-center justify-center group-hover:bg-zinc-200 transition-colors">
              <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default WebinarCard;
